import { useMemo } from "react";
import {
	CountStyle,
	CustomQuoteHistoryAccordion,
	CustomQuoteHistoryContent,
	CustomQuoteHistoryItemBox,
	CustomQuoteHistoryItemContent,
	CustomQuoteHistoryItemHeader,
	CustomQuoteHistoryPriceText,
	CustomQuoteHistoryWrapper,
	CustomQuoteItemDetailField,
	CustomQuoteLineItemGrid,
	DeleteButton,
	HistoryHeaderLeft,
	HistoryHeaderRight,
	HistoryHeaderText,
	ResponsiveDivider
} from "./CustomQuoteDetailHistory.styles.ts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	CustomQuoteHistoryText,
	HistoryTotalPrice,
	HistoryViewQuote,
	NoAvailableQuoteHistory,
	NoAvailableQuoteHistorySubtitle
} from "../../NewOrders/constants.ts";
import { models } from "types/api/viewModels.ts";
import { AccordionDetails, AccordionSummary, Button, Chip } from "@mui/material";
import { CustomQuoteRequestNoResultsFoundContainer } from "../CustomQuoteRequestTable.styles.ts";
import { ReviseButtonText, UnavailableDataPlaceholderText } from "constants/text.ts";
import { Header, Modification } from "components/Common/LineItemGrid";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { format } from "date-fns";
import { formatPriceWithPrefix } from "utils/string.ts";
import LinkButton from "components/Common/Link/LinkButton.tsx";
import { sortByTimestampDesc } from "../customQuoteUtils.ts";
import {
	BuildNewOrderLineItemsHeader,
	BuildNewOrderLineItemsHeaderWrapper
} from "components/NewOrders/NewOrders.styles.ts";
import { getAttributes, getSubheaderAttributes } from "components/NewOrders/utils/NewOrderUtils.tsx";
import { useNavigate, useParams } from "react-router";
import { useDialog } from "hooks/useDialog.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCreateRevisionMutation } from "features/api/customQuotesApi.ts";
import useAttachments from "hooks/useAttachments.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";

interface CustomQuoteHistoryProps {
	revisions: models["CustomQuoteViewModel"]["revisions"] | null;
	status?: models["CustomQuoteViewModel"]["status"];
	userRole?: string | null;
	customQuoteData?: models["CustomQuoteViewModel"] | null;
}

export const CustomQuoteHistory = ({ revisions, status, userRole, customQuoteData }: CustomQuoteHistoryProps) => {
	const safeRevisions = revisions ?? [];
	const role: "USER" | "QUOTER" = userRole === "QUOTER" ? "QUOTER" : "USER";
	const isQuoter = role === "QUOTER";
	const hasQuotes = safeRevisions.length > 0;
	const navigate = useNavigate();
	const params = useParams();
	const quoteId = params.quoteId;
	const { openDialog } = useDialog();
	const [createRevision, { error: createRevisionError }] = useCreateRevisionMutation();

	const attachmentsMap = useMemo(
		() =>
			new Map(
				customQuoteData?.revisions
					?.filter((r) => r.pdfFileUrl && r.revisionId)
					.map((r) => [r.revisionId!, r.pdfFileUrl!]) ?? []
			),
		[customQuoteData?.revisions]
	);

	const { attachmentsWithUrls } = useAttachments({
		containerId: quoteId ?? "",
		attachmentsMap
	});

	const getPdfUrlForRevision = (revisionId: number | undefined) => {
		const attachment = attachmentsWithUrls.find((att) => att.fileId === revisionId);
		return attachment?.blobUrl;
	};

	if (!hasQuotes && isQuoter) return null;

	const sortedRevisions = sortByTimestampDesc(safeRevisions);

	const formatTimestamp = (timestamp?: string): string =>
		`Last Updated on ${timestamp ? format(new Date(timestamp), "MM/dd/yy") : UnavailableDataPlaceholderText}`;

	const isExpired = (quote: (typeof safeRevisions)[0]): boolean => {
		const quoteStatus = (quote as any).status;
		return quoteStatus?.toUpperCase() === "EXPIRED";
	};

	const editRevision = (revisionId: number) => {
		navigate(`add-quote?revisionId=${revisionId}`);
	};

	const deleteRevision = async (revisionId: number) => {
		openDialog({
			dialogStatus: DialogTracking.DELETE_CUSTOM_QUOTE_REVISION,
			dialogId: quoteId,
			dialogData: revisionId
		});
	};

	const addNewCQRevision = async (sourceRevisionId: number) => {
		const revision: models["CreateRevisionRequest"] = {
			sourceRevisionId: sourceRevisionId
		};
		await createRevision({ quoteId: customQuoteData?.quoteId ?? "", revision }).then((response) => {
			const revisions = response?.data?.revisions ?? [];
			const sortedRevision = sortByTimestampDesc(revisions);
			const revisionId = sortedRevision?.[0]?.revisionId ?? 0;
			navigate(`/custom-quote/${customQuoteData?.quoteId}/add-quote?revisionId=${revisionId}`);
		});
	};

	return (
		<CustomQuoteHistoryWrapper>
			<CustomQuoteHistoryAccordion
				data-testid="custom-quote-history-accordion"
				defaultExpanded={true}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>{CustomQuoteHistoryText}</AccordionSummary>

				<AccordionDetails>
					{!hasQuotes ? (
						!isQuoter && (
							<CustomQuoteRequestNoResultsFoundContainer>
								<img
									src="/assets/CustomQuoteNoResults.svg"
									alt="No Results Found"
									data-testid="custom-quote-no-results-found"
								/>
								<div>{NoAvailableQuoteHistory}</div>
								<div>{NoAvailableQuoteHistorySubtitle}</div>
							</CustomQuoteRequestNoResultsFoundContainer>
						)
					) : (
						<CustomQuoteHistoryContent>
							<div style={{ paddingTop: "10px" }}></div>
							{sortedRevisions.map((revision, index) => {
								const count = sortedRevisions.length - index;
								return (
									<>
										{index !== 0 && <ResponsiveDivider />}
										<CustomQuoteHistoryItemBox
											key={revision.revisionId ?? index}
											data-testid="custom-quote-history-revisions"
										>
											<CustomQuoteHistoryItemHeader>
												<HistoryHeaderLeft>
													<HistoryHeaderText>
														<CountStyle>{count}</CountStyle>
														CQ #{revision.revisionId}
														{!isQuoter || (isQuoter && revision.pdfFileUrl) ? (
															<LinkButton
																onClick={() =>
																	window.open(
																		getPdfUrlForRevision(revision.revisionId) ?? "",
																		"_blank",
																		"noopener,noreferrer"
																	)
																}
																data-testid={`view-quote-link-${revision.revisionId}`}
															>
																<OpenInNewIcon
																	fontSize="small"
																	style={{ marginRight: 4 }}
																	data-testid="custom-quote-open-in-new-icon"
																/>
																{HistoryViewQuote}
															</LinkButton>
														) : (
															<>
																<LinkButton
																	onClick={() =>
																		editRevision(revision.revisionId ?? 0)
																	}
																>
																	Edit
																</LinkButton>
																<DeleteButton
																	onClick={() =>
																		deleteRevision(revision.revisionId ?? 0)
																	}
																	variant="text"
																>
																	<DeleteIcon />
																	<span>Delete</span>
																</DeleteButton>
															</>
														)}
													</HistoryHeaderText>

													{isExpired(revision) && (
														<Chip
															label="Expired"
															color="error"
															size="small"
															data-testid="quote-expired-chip"
														/>
													)}
												</HistoryHeaderLeft>

												<HistoryHeaderRight>
													{formatTimestamp(revision.timestamp)}
												</HistoryHeaderRight>
											</CustomQuoteHistoryItemHeader>

											<CustomQuoteHistoryItemContent>
												<BuildNewOrderLineItemsHeaderWrapper>
													<BuildNewOrderLineItemsHeader>
														<div data-testid="custom-quote-build-label">
															<div>{getAttributes(revision)}</div>
															<div>{getSubheaderAttributes(revision ?? {})}</div>
														</div>
													</BuildNewOrderLineItemsHeader>
												</BuildNewOrderLineItemsHeaderWrapper>
												{revision.lineItem && (
													<CustomQuoteLineItemGrid
														mainRowContent={[
															<Header
																key="base-sku-header"
																item={revision.lineItem}
															/>,
															<CustomQuoteItemDetailField key="base-sku-ext-list-price">
																<div>{HistoryTotalPrice}</div>
																<CustomQuoteHistoryPriceText data-testid="custom-quote-ext-list-price">
																	{formatPriceWithPrefix(revision.price)}
																</CustomQuoteHistoryPriceText>
															</CustomQuoteItemDetailField>,
															isQuoter && (
																<CustomQuoteItemDetailField key="cq-revise-quote">
																	<Button
																		type="submit"
																		variant="outlined"
																		data-testid="revise-custom-quote"
																		form="revise-cq-item-form"
																		onClick={() =>
																			addNewCQRevision(revision?.revisionId ?? 0)
																		}
																	>
																		{ReviseButtonText}
																	</Button>
																</CustomQuoteItemDetailField>
															)
														]}
														modificationRows={revision.lineItem.modifications?.map(
															(modification) => (
																<Modification
																	description={modification.description}
																	key={modification.sku}
																	lineNumber={modification.lineItemNumber}
																	title={modification.sku}
																	values={modification.values}
																/>
															)
														)}
														requiredCategoriesRows={revision.lineItem.requiredCategories?.map(
															(category) => (
																<Modification
																	key={category.categoryName}
																	requiredCategory={category}
																	lineNumber={undefined}
																/>
															)
														)}
													/>
												)}
											</CustomQuoteHistoryItemContent>
										</CustomQuoteHistoryItemBox>
									</>
								);
							})}
						</CustomQuoteHistoryContent>
					)}
				</AccordionDetails>
			</CustomQuoteHistoryAccordion>
		</CustomQuoteHistoryWrapper>
	);
};
